import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {CheckboxCard} from 'Quoting/components/shared/CheckboxCard';
import {groupByKey} from 'Quoting/utils/utilities';
import {themeColors} from 'Theme';
import React, {Fragment, useState} from 'react';

const useSelectionListStyles = makeStyles(() => ({
  card: {
    boxShadow: 'none',
    padding: '1rem',
  },
  secondaryTitle: {
    color: '#2F3847',
    fontSize: '1.125rem',
    lineHeight: '1.35rem',
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  cardContent: {
    paddingBottom: '2rem',
    borderBottom: '1.5px solid #cccccc',
  },
  cardActions: {
    justifyContent: 'flex-end',
    paddingTop: '1.5rem',
    fontSize: 500,
  },
  cardActionCancel: {
    backgroundColor: themeColors.gray_100,
    color: themeColors.gray_800,
    borderRadius: '5px',
    marginRight: '0.65rem',
    fontSize: 500,
    textTransform: 'capitalize',
  },
  cardActionContinue: {
    backgroundColor: themeColors.blue_400,
    color: themeColors.primaryWhite,
    borderRadius: '5px',
    fontSize: 500,
    textTransform: 'capitalize',

    '&:hover': {
      backgroundColor: '#2FA0FF',
      color: themeColors.primaryWhite,
    },
  },
  item: {
    minWidth: 'fit-content',
  },
  groupDivider: {
    width: '100%',
    margin: '1rem 0',
    '&:last-of-type': {
      display: 'none',
    },
  },
}));

export type SelectionItem = {
  disabled?: boolean;
  label?: React.ReactNode;
  key?: string;
};

type Header = {
  primaryTitle?: React.ReactNode;
  secondaryTitle?: React.ReactNode;
};

export type SelectionListProps<T> = {
  items: (T & SelectionItem)[];
  identifier: string | number;
  groupBy?: {
    key: string | number;
    labels: Map<string, string>;
  };
  header: Header;
  onCancel: () => void;
  onContinue: (identifier: string | number) => void;
};

export const SelectionList = <T,>(props: SelectionListProps<T>) => {
  const [selectedItem, setSelectedItem] = useState<string | number | null>(null);
  const classes = useSelectionListStyles({isEnabled: selectedItem !== null});

  return (
    <Card className={classes.card}>
      {props.header.primaryTitle && (
        <CardHeader title={<span className="primaryTitle">{props.header.primaryTitle}</span>} />
      )}
      <CardContent className={classes.cardContent}>
        {props.header.secondaryTitle && (
          <Typography className="secondaryTitle">{props.header.secondaryTitle}</Typography>
        )}
        {props.items && Array.isArray(props.items) && (
          <Grid container spacing={2}>
            {props.groupBy ? (
              <>
                {Object.entries(groupByKey(props.items, props.groupBy?.key)).map(([key, value]) => (
                  <Fragment key={key}>
                    <div style={{display: 'flex', flexDirection: 'column', width: '100%'}}>
                      <Typography variant="body1" style={{color: '#2F3847', fontWeight: '700'}}>
                        {props.groupBy?.labels.get(key)}
                      </Typography>

                      <Grid container spacing={2}>
                        {Array.isArray(value) &&
                          value.map(item => (
                            <Grid item key={item.key} xs={12} sm={12} md={6} lg={4} className={classes.item}>
                              <CheckboxCard
                                selected={!item.disabled && selectedItem === item[props.identifier]}
                                onChange={() => !item.disabled && setSelectedItem(item[props.identifier])}
                                label={item.label ? item.label : null}
                                disabled={item.disabled}
                              />
                            </Grid>
                          ))}
                      </Grid>
                    </div>
                    <Divider className={classes.groupDivider} />
                  </Fragment>
                ))}
              </>
            ) : (
              <>
                {props.items.map(item => (
                  <Grid item key={item.key} xs={12} sm={12} md={6} lg={4} className={classes.item}>
                    <CheckboxCard
                      selected={!item.disabled && selectedItem === item[props.identifier]}
                      onChange={() => !item.disabled && setSelectedItem(item[props.identifier])}
                      label={item.label ? item.label : null}
                      disabled={item.disabled}
                    />
                  </Grid>
                ))}
              </>
            )}
          </Grid>
        )}
      </CardContent>
      <CardActions className={classes.cardActions}>
        <Button onClick={props.onCancel} className={classes.cardActionCancel} variant="contained" disableElevation>
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (selectedItem !== null) {
              props.onContinue(selectedItem);
            }
          }}
          disabled={selectedItem === null}
          variant="contained"
          disableElevation
          className={classes.cardActionContinue}
        >
          Continue
        </Button>
      </CardActions>
    </Card>
  );
};
